import React, { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import AdminLayout from "@/components/admin/AdminLayout";
import axios from "axios";
import { MessageSquare, Phone, Clock, CheckCircle, Eye, X, User } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const AVA_HEADSHOT = "https://customer-assets.emergentagent.com/job_app-constructor-85/artifacts/ngv4sm7y_AvA%20Headshot.png";

const STATUS_COLORS = {
  active:    "bg-blue-50 text-blue-700 border-blue-100",
  completed: "bg-emerald-50 text-emerald-700 border-emerald-100",
  abandoned: "bg-slate-100 text-slate-500 border-slate-200",
  pending:   "bg-amber-50 text-amber-700 border-amber-100",
  initiated: "bg-emerald-50 text-emerald-700 border-emerald-100",
  failed:    "bg-red-50 text-red-500 border-red-100",
};

function ConversationModal({ session, onClose }) {
  if (!session) return null;
  const messages = session.messages || [];
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-lg max-h-[85vh] flex flex-col overflow-hidden">
        <div className="bg-gradient-to-r from-blue-600 to-cyan-500 px-5 py-4 flex items-center gap-3">
          <div className="w-9 h-9 rounded-full overflow-hidden ring-2 ring-white/30">
            <img src={AVA_HEADSHOT} alt="AvA" className="w-full h-full object-cover object-top" />
          </div>
          <div className="flex-1">
            <div className="text-white font-semibold text-sm">AvA Conversation</div>
            <div className="text-white/70 text-xs">{new Date(session.created_at).toLocaleString()}</div>
          </div>
          <button onClick={onClose} className="p-1.5 text-white/70 hover:text-white hover:bg-white/10 rounded-lg transition-colors">
            <X size={16} />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-4 bg-slate-50 space-y-3">
          {messages.length === 0 ? (
            <p className="text-center text-sm text-slate-400 py-8">No messages in this session.</p>
          ) : messages.map((msg, i) => {
            const isAvA = msg.role === "assistant";
            const display = (msg.content || "").replace(/<INTAKE_COMPLETE>[\s\S]*/g, "").trim();
            if (!display) return null;
            return (
              <div key={i} className={`flex ${isAvA ? "justify-start" : "justify-end"}`}>
                {isAvA && (
                  <div className="w-7 h-7 rounded-full overflow-hidden flex-shrink-0 mr-2 mt-1">
                    <img src={AVA_HEADSHOT} alt="AvA" className="w-full h-full object-cover object-top" />
                  </div>
                )}
                <div className={`max-w-[80%] px-3.5 py-2.5 rounded-2xl text-xs leading-relaxed ${
                  isAvA ? "bg-white border border-slate-100 shadow-sm text-slate-700 rounded-tl-sm"
                        : "bg-blue-600 text-white rounded-tr-sm"
                }`}>
                  {display}
                </div>
              </div>
            );
          })}
        </div>
        <div className="px-5 py-3 border-t border-slate-100 flex items-center justify-between bg-white">
          <div className="flex items-center gap-3 text-xs text-slate-400">
            <span>{messages.length} message{messages.length !== 1 ? "s" : ""}</span>
            {session.lead_id && (
              <a href={`/admin/leads/${session.lead_id}`}
                className="flex items-center gap-1 text-blue-600 font-medium hover:text-blue-700">
                <CheckCircle size={12} /> View Lead
              </a>
            )}
          </div>
          <span className={`px-2.5 py-1 rounded-full text-xs font-medium border ${STATUS_COLORS[session.status] || "bg-slate-100 text-slate-500 border-slate-200"}`}>
            {session.status}
          </span>
        </div>
      </div>
    </div>
  );
}

export default function AdminSessions() {
  const { authHeader } = useAuth();
  const [tab, setTab] = useState("chats");
  const [sessions, setSessions] = useState([]);
  const [callRequests, setCallRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewSession, setViewSession] = useState(null);
  const [loadingSession, setLoadingSession] = useState(false);

  useEffect(() => {
    if (tab === "chats") {
      setLoading(true);
      axios.get(`${API}/ava/sessions`, { headers: authHeader() })
        .then(r => setSessions(r.data.sessions || []))
        .finally(() => setLoading(false));
    } else {
      setLoading(true);
      axios.get(`${API}/retell/requests`, { headers: authHeader() })
        .then(r => setCallRequests(r.data.requests || []))
        .finally(() => setLoading(false));
    }
  }, [tab, authHeader]);

  const openSession = async (session_id) => {
    setLoadingSession(true);
    try {
      const res = await axios.get(`${API}/ava/sessions/${session_id}/detail`, { headers: authHeader() });
      setViewSession(res.data);
    } catch {}
    setLoadingSession(false);
  };

  return (
    <AdminLayout title="AvA Sessions">
      <div className="space-y-4">
        {/* Tabs */}
        <div className="bg-white rounded-2xl border border-slate-100 shadow-[0_4px_20px_rgb(0,0,0,0.04)] p-1.5 flex gap-1 w-fit">
          <button onClick={() => setTab("chats")}
            className={`flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 ${
              tab === "chats" ? "bg-gradient-to-r from-blue-600 to-cyan-500 text-white shadow-md" : "text-slate-500 hover:text-slate-700"
            }`}>
            <MessageSquare size={15} /> AvA Chat Sessions
          </button>
          <button onClick={() => setTab("calls")}
            className={`flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 ${
              tab === "calls" ? "bg-gradient-to-r from-blue-600 to-cyan-500 text-white shadow-md" : "text-slate-500 hover:text-slate-700"
            }`}>
            <Phone size={15} /> Voice Call Requests
          </button>
        </div>

        {/* Chat Sessions Table */}
        {tab === "chats" && (
          <div className="bg-white rounded-2xl border border-slate-100 shadow-[0_4px_20px_rgb(0,0,0,0.04)] overflow-hidden">
            <div className="px-5 py-4 border-b border-slate-50">
              <h3 className="text-sm font-semibold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                AvA Intake Sessions
                <span className="text-slate-400 font-normal ml-2">({sessions.length})</span>
              </h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50/80 border-b border-slate-100">
                  <tr>
                    {["Session ID", "Messages", "Status", "Lead", "Started", ""].map(h => (
                      <th key={h} className="text-left px-5 py-3 text-xs font-medium text-slate-400">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-50">
                  {loading ? (
                    <tr><td colSpan={6} className="px-5 py-12 text-center text-slate-400 text-sm">Loading sessions...</td></tr>
                  ) : sessions.length === 0 ? (
                    <tr><td colSpan={6} className="px-5 py-16 text-center">
                      <div className="flex flex-col items-center gap-2">
                        <div className="w-10 h-10 rounded-2xl bg-blue-50 flex items-center justify-center">
                          <MessageSquare size={18} className="text-blue-400" />
                        </div>
                        <p className="text-sm text-slate-400">No sessions yet. AvA conversations will appear here.</p>
                      </div>
                    </td></tr>
                  ) : sessions.map(s => (
                    <tr key={s.session_id} className="hover:bg-slate-50/50 group transition-colors">
                      <td className="px-5 py-3.5 font-mono text-xs text-slate-500">{s.session_id?.slice(0, 16)}…</td>
                      <td className="px-5 py-3.5 text-slate-700 font-medium">{s.message_count || 0}</td>
                      <td className="px-5 py-3.5">
                        <span className={`px-2.5 py-1 rounded-full text-xs font-medium border ${STATUS_COLORS[s.status] || "bg-slate-100 text-slate-500 border-slate-200"}`}>
                          {s.status}
                        </span>
                      </td>
                      <td className="px-5 py-3.5">
                        {s.lead_id ? (
                          <a href={`/admin/leads/${s.lead_id}`}
                            className="flex items-center gap-1 text-xs text-emerald-600 font-medium hover:text-emerald-700">
                            <CheckCircle size={11} /> View Lead
                          </a>
                        ) : <span className="text-xs text-slate-300">—</span>}
                      </td>
                      <td className="px-5 py-3.5 text-slate-400 text-xs">
                        {s.created_at ? new Date(s.created_at).toLocaleString() : "—"}
                      </td>
                      <td className="px-5 py-3.5 text-right">
                        <button onClick={() => openSession(s.session_id)} disabled={loadingSession}
                          className="inline-flex items-center gap-1 text-xs text-blue-500 hover:text-blue-700 font-medium opacity-0 group-hover:opacity-100 transition-opacity">
                          <Eye size={12} /> View
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Call Requests Table */}
        {tab === "calls" && (
          <div className="bg-white rounded-2xl border border-slate-100 shadow-[0_4px_20px_rgb(0,0,0,0.04)] overflow-hidden">
            <div className="px-5 py-4 border-b border-slate-50">
              <h3 className="text-sm font-semibold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Voice Call Requests
                <span className="text-slate-400 font-normal ml-2">({callRequests.length})</span>
              </h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50/80 border-b border-slate-100">
                  <tr>
                    {["Name", "Phone", "Email", "Company", "Best Time", "Status", "Requested"].map(h => (
                      <th key={h} className="text-left px-5 py-3 text-xs font-medium text-slate-400">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-50">
                  {loading ? (
                    <tr><td colSpan={7} className="px-5 py-12 text-center text-slate-400 text-sm">Loading...</td></tr>
                  ) : callRequests.length === 0 ? (
                    <tr><td colSpan={7} className="px-5 py-16 text-center">
                      <div className="flex flex-col items-center gap-2">
                        <div className="w-10 h-10 rounded-2xl bg-cyan-50 flex items-center justify-center">
                          <Phone size={18} className="text-cyan-400" />
                        </div>
                        <p className="text-sm text-slate-400">No call requests yet.</p>
                      </div>
                    </td></tr>
                  ) : callRequests.map(r => (
                    <tr key={r.id} className="hover:bg-slate-50/50 transition-colors">
                      <td className="px-5 py-3.5 font-medium text-slate-900">{r.first_name} {r.last_name}</td>
                      <td className="px-5 py-3.5 text-slate-600 font-mono text-xs">{r.phone}</td>
                      <td className="px-5 py-3.5 text-slate-500 text-xs">{r.email}</td>
                      <td className="px-5 py-3.5 text-slate-500 text-xs">{r.company_name || "—"}</td>
                      <td className="px-5 py-3.5 text-slate-500 text-xs">{r.best_time}</td>
                      <td className="px-5 py-3.5">
                        <span className={`px-2.5 py-1 rounded-full text-xs font-medium border ${STATUS_COLORS[r.status] || "bg-slate-100 text-slate-500 border-slate-200"}`}>
                          {r.status}
                        </span>
                      </td>
                      <td className="px-5 py-3.5 text-slate-400 text-xs">
                        {r.created_at ? new Date(r.created_at).toLocaleDateString() : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Conversation viewer modal */}
      {viewSession && <ConversationModal session={viewSession} onClose={() => setViewSession(null)} />}
    </AdminLayout>
  );
}
